import { VEHICLE_ACCESS_USER_SEARCH } from "../constants/vehicle-access-user-search";
import {
	toVehicleAccess,
	toVehicleAccessGrant,
	toVehicleAccessGrantRemoval,
} from "../utils/mappers/to-vehicle-access";
import { toVehicleAccessCandidate } from "../utils/mappers/to-vehicle-access-candidate";
import type {
	GiveVehicleAccessParams,
	RemoveVehicleAccessParams,
	VehicleAccess,
	VehicleAccessCandidate,
	VehicleAccessGrant,
	VehicleAccessGrantRemoval,
} from "../types/vehicle-access.types";
import {
	deleteVehicleAccessGrant,
	getBookingUsers,
	getVehicleAccess,
	getVehicleEvents,
	putVehicleAccessGrant,
} from "@/lib/api";

/**
 * Vehicle access grants: who holds rights on a vehicle beyond its public
 * availability. BattAdmin-only endpoints, keyed on the vehicle id and the
 * booking-engine remote id.
 */
export const vehicleAccessService = {
	async getVehicleAccess(vehicleId: string): Promise<VehicleAccess> {
		const response = await getVehicleAccess(vehicleId);
		return toVehicleAccess(response);
	},

	/** The vehicle's access changes, newest first. DTOs: the hook maps them. */
	async getVehicleAccessChangesPaginated(
		vehicleId: string,
		page: number,
		size: number
	) {
		const response = await getVehicleEvents(vehicleId, { page, size });
		return {
			items: response.items,
			totalElements: response.totalElements,
		};
	},

	/**
	 * Deliberately no membership filter: a grant can go to any user on the
	 * platform, with or without a Membership.
	 */
	async searchUsers(userName: string): Promise<Array<VehicleAccessCandidate>> {
		const response = await getBookingUsers({
			userName,
			size: VEHICLE_ACCESS_USER_SEARCH.maxResults,
		});
		return response.items
			.map(toVehicleAccessCandidate)
			.filter((user): user is VehicleAccessCandidate => user !== null);
	},

	/** Gives the level, or changes it for a user who already holds a grant. */
	async giveAccess({
		vehicleId,
		userRemoteId,
		level,
	}: GiveVehicleAccessParams): Promise<VehicleAccessGrant> {
		const response = await putVehicleAccessGrant(vehicleId, userRemoteId, {
			level,
		});
		return toVehicleAccessGrant(response);
	},

	/**
	 * Idempotent: a user who held no grant gives `removed: false` rather than
	 * a 404. The BFF notifies #ops when a grant is actually removed.
	 */
	async removeAccess({
		vehicleId,
		userRemoteId,
	}: RemoveVehicleAccessParams): Promise<VehicleAccessGrantRemoval> {
		const response = await deleteVehicleAccessGrant(vehicleId, userRemoteId);
		return toVehicleAccessGrantRemoval(response);
	},
};
